<template>
  <div class="wizard-container">
    <div
      class="steps-nav-container"
      :style="{ '--sticky-tab-top': `${contentTop}px` }"
    >
      <Icon
        type="md-close"
        class="close-icon"
        @click="returnToQueryPad"
      />
      <Steps
        :current="currentStep"
        class="steps"
      >
        <Step
          title="Query Information"
          content="Fill in meatadata like name, description..."
        />
        <Step
          title="Query Inputs"
          content="Set up the inputs for the query, eg. Region"
        />
        <Step
          title="Template Test"
          content="Test the template to see if it works"
        />
        <Step
          title="Template Submission"
          content="Submit and share the template"
        />
      </Steps>
    </div>

    <div class="steps-form-container">
      <Form
        v-if="currentStep === 0"
        :model="queryMetaData"
        :label-width="150"
        :rules="ruleValidate"
        class="step-form"
      >
        <FormItem
          prop="name"
          label="Query Title"
        >
          <Input
            v-model="queryMetaData.name"
            placeholder="Title for the query template ..."
          />
        </FormItem>
        <FormItem
          prop="description"
          label="Query Description"
        >
          <Input
            v-model="queryMetaData.description"
            placeholder="Short description about the usage of the query template..."
          />
        </FormItem>
        <FormItem
          prop="tags"
          label="Categories"
        >
          <Select
            v-model="queryMetaData.tags"
            multiple
            filterable
            clearable
          >
            <Option
              v-for="category in goldenQuery.categories"
              :key="category"
              :label="category"
              :value="category"
            />
          </Select>
        </FormItem>
        <FormItem
          prop="author.name"
          label="Author Name"
        >
          <Select
            v-model="queryMetaData.author.name"
            placeholder="Author Name"
            filterable
            clearable
          >
            <Option
              v-for="user in users"
              :key="user.id"
              :label="user.name"
              :value="user.name"
            />
          </Select>
        </FormItem>
        <FormItem
          prop="date"
          label="Created Date"
        >
          <Input
            v-model="queryMetaData.date"
            placeholder="Created Date"
          />
        </FormItem>
        <FormItem label="Query Preview">
          <codemirror
            :value="queryMetaData.rawQuery"
            :options="cmOptionsPreview"
            class="query-container"
          />
        </FormItem>
        <FormItem>
          <Button
            type="primary"
            :disabled="!metaDataFilled()"
            style="margin-right: 8px"
            @click="nextStepToParamSelection"
          >
            Continue
          </Button>
          <Button @click="returnToQueryPad">
            Cancel
          </Button>
        </FormItem>
      </Form>

      <Form
        v-else-if="currentStep === 1"
        :model="queryMetaData"
        :label-width="150"
        class="step-form"
      >
        <FormItem label="Instructions">
          <div class="instruction-wrapper">
            <p
              v-for="(message,index) in notice.inputSelection"
              :key="index"
            >
              {{ message }}
            </p>
          </div>
        </FormItem>
        <FormItem
          label="Inputs"
          class="query-parameters-wrapper"
        >
          <Select
            ref="selection"
            v-model="queryMetaData.params"
            filterable
            clearable
            class="select-query-parameters"
            @on-change="onAddSelectedParams"
          >
            <Option
              v-for="paramName in Object.keys(goldenQuery.parameters)"
              v-show="goldenQuery.parameters[paramName].isSelectable"
              :key="paramName"
              :label="paramName"
              :value="paramName"
            />
          </Select>
          <Tooltip
            content="Define Your Own Input"
            placement="right"
          >
            <Icon
              type="ios-add-circle-outline"
              class="icon-add-parameter"
              @click="customParamModal = true"
            />
          </Tooltip>
          <Modal
            v-model="customParamModal"
            title="Customize the Input"
            class="custom-param-modal"
            @on-ok="onAddCustomParam"
            @on-cancel="onRemoveCustomParam"
          >
            <FormItem
              label="Name"
              :required="true"
            >
              <Input
                v-model="customParam.name"
                placeholder="Name of input"
                clearable
              />
            </FormItem>
            <FormItem
              label="Description"
              :required="true"
            >
              <Input
                v-model="customParam.description"
                placeholder="Short description of input"
                clearable
              />
            </FormItem>
            <FormItem label="Type">
              <Select
                v-model="customParam.type"
                filterable
                clearable
              >
                <Option
                  label="String"
                  value="string"
                />
                <Option
                  label="Keyword"
                  value="keyword"
                />
                <Option
                  label="Number"
                  value="number"
                />
                <Option
                  label="Decimal"
                  value="decimal"
                />
                <Option
                  label="Date"
                  value="date"
                />
                <Option
                  label="Datetime"
                  value="datetime"
                />
              </Select>
            </FormItem>
            <FormItem
              v-if="customParam.type === 'keyword'"
              label="Options"
              :required="true"
            >
              <Input
                v-model="customKeywordOptions"
                placeholder="Enter options for the keyword, separated by ','"
                clearable
              />
            </FormItem>
            <FormItem label="Default">
              <Input
                v-model="customParam.default"
                placeholder="Default value of input if any"
                clearable
              />
            </FormItem>
            <FormItem label="Optional">
              <Checkbox v-model="customParam.optional" />
            </FormItem>
          </Modal>
        </FormItem>
        <FormItem>
          <Row class="selected-param-list">
            <Tooltip
              v-for="(selectedParamName, index) in selectedParamNames"
              :key="index"
              placement="bottom"
              max-width="500"
              :content="goldenQuery.parameters[selectedParamName].description"
            >
              <Tag
                ref="paramName"
                :name="selectedParamName"
                :checked="selectedParamName === targetParamName"
                :color="selectedParamName === targetParamName ? 'success' : 'default'"
                closable
                checkable
                @on-close="onRemoveSelectedParams"
                @on-change="(checked, name) => targetParamName = checked ? name : ''"
              >
                {{ selectedParamName }}
              </Tag>
            </Tooltip>
          </Row>
        </FormItem>
        <FormItem>
          <codemirror
            ref="cmParam"
            :value="queryMetaData.lines || queryMetaData.rawQuery"
            :options="cmOptionsParamSelection"
            class="query-container"
            @mouseover.stop.native="highlightToken"
            @cursorActivity.self="replaceToken"
          />
          <codemirror
            ref="cmPreview"
            :value="queryMetaData.rawQuery"
            :options="cmOptionsPreview"
            class="query-container-invisible"
            style="display:none;"
          />
        </FormItem>
        <FormItem>
          <Button
            style="margin-right: 8px;"
            @click="previousStep"
          >
            Previous
          </Button>
          <Button
            type="primary"
            @click="nextStepToTest"
          >
            Continue
          </Button>
        </FormItem>
      </Form>

      <Form
        v-else-if="currentStep === 2"
        :label-width="0"
        class="step-form"
      >
        <FormItem>
          <h2>Fill in the inputs and test the template:</h2>
        </FormItem>
        <FormItem>
          <QueryTemplate
            :query="queryMetaDataCopy"
            :golden-query-params="goldenQueryParams"
            is-wizard
            @has-results="checkResults"
          />
        </FormItem>
        <FormItem>
          <Button
            style="margin-right: 8px;"
            @click="previousStep"
          >
            Previous
          </Button>
          <Button
            type="primary"
            :disabled="!testPassed"
            @click="nextStep"
          >
            Continue
          </Button>
        </FormItem>
      </Form>

      <Form
        v-else-if="currentStep === 3"
        :label-width="150"
        class="step-form"
      >
        <h2 style="margin-left: 50px;">
          Template Meta Data Preview:
        </h2>
        <div class="meta-data-preview-wrapper">
          <Card class="meta-data-preview-item">
            <div class="meta-data-preview-item-label">
              Query Name
            </div>
            <div class="meta-data-preview-item-content">
              {{ queryMetaData.name }}
            </div>
          </Card>
          <Card class="meta-data-preview-item">
            <div class="meta-data-preview-item-label">
              Query Description
            </div>
            <div class="meta-data-preview-item-content">
              {{ queryMetaData.description }}
            </div>
          </Card>
          <Card class="meta-data-preview-item">
            <div class="meta-data-preview-item-label">
              Tags
            </div>
            <div class="meta-data-preview-item-content">
              {{ queryMetaData.tags }}
            </div>
          </Card>
          <Card class="meta-data-preview-item">
            <div class="meta-data-preview-item-label">
              Author
            </div>
            <div class="meta-data-preview-item-content">
              {{ queryMetaData.author.name }} | {{ queryMetaData.author.email }}
            </div>
          </Card>
          <Card class="meta-data-preview-item">
            <div class="meta-data-preview-item-label">
              Inputs
            </div>
            <div class="meta-data-preview-item-content">
              <Table
                border
                :columns="paramColumns"
                :data="queryMetaData.params"
              />
            </div>
          </Card>
          <Card class="meta-data-preview-item">
            <div class="meta-data-preview-item-label">
              Query Text Preview
            </div>
            <codemirror
              :value="queryMetaData.lines"
              :options="cmOptionsPreview"
              class="query-container"
              style="width: 600px; margin-top: 20px;"
            />
          </Card>
          <Row class="final-step-buttons">
            <Button
              style="margin-right: 8px;"
              @click="previousStep"
            >
              Previous
            </Button>
            <Button
              type="success"
              @click="submitTemplate"
            >
              Submit Template
            </Button>
          </Row>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'

import QueryTemplate from './helper-components/QueryTemplate'
import { parameter, category } from '@/constants/golden-query-constants'
import { setUpQueryFormat } from '@/helpers/query-formatter'
import UserMixin from '@/mixins/UserMixin'

const DEFAULT_CUSTOM_PARAMETER = { optional: false, isSelectable: true }

export default {
  components: {
    QueryTemplate
  },

  mixins: [
    UserMixin
  ],

  props: {
    contentTop: Number,
    queryText: String,
    queryName: String
  },

  data () {
    const initialQueryText = this.$root.preferences.queryPad.queryTemplate.rawQuery || this.queryText
    return {
      defaultUser: this.$auth.getUser(),
      currentStep: 0,
      queryMetaData: {
        name: this.$root.preferences.queryPad.queryTemplate.name || this.queryName,
        description: this.$root.preferences.queryPad.queryTemplate.description || '',
        tags: this.$root.preferences.queryPad.queryTemplate.tags || [],
        author: this.$root.preferences.queryPad.queryTemplate.author || { name: '', email: '' },
        date: this.$root.preferences.queryPad.queryTemplate.date || this.$moment().format('YYYY-MM-DD HH:mm:ss'),
        params: [],
        rawQuery: initialQueryText,
        lines: initialQueryText,
        submitterId: this.$auth.getUser().id
      },
      queryMetaDataCopy: null,
      ruleValidate: {
        name: [
          { required: true, message: 'The name cannot be empty', trigger: 'blur' }
        ],
        description: [
          { required: true, message: 'Description cannot be empty', trigger: 'blur' }
        ],
        tags: [
          { required: true, type: 'array', min: 1, message: 'Please select at least one tag', trigger: 'change' }
        ],
        'author.name': [
          { required: true, type: 'string', message: 'Please select the author name', trigger: 'change' }
        ],
        date: [
          { required: true, message: 'Created date cannot be empty', trigger: 'blur' }
        ]
      },
      cmOptionsPreview: {
        mode: 'text/x-sql',
        lineNumbers: true,
        readOnly: true,
        autoRefresh: true
      },
      cmOptionsParamSelection: {
        mode: 'text/x-sql',
        readOnly: true,
        lineNumbers: true,
        autoRefresh: true,
        autofocus: true
      },
      hoverTextMarker: null,
      targetMarkers: null,
      goldenQuery: {
        parameters: {},
        categories: []
      },
      selectedParamNames: [],
      targetParamName: '',
      paramsPosMap: {},
      paramColumns: null,
      testPassed: false,
      notice: {
        inputSelection: [
          '1. Select an input name',
          '2. Hover over the query text and see the highlighted token',
          '3. Right click the correct token to replace it',
          'You can also:',
          '- Click on the input tags to switch the inputs',
          '- Click the cross icon on the input tags to remove it, any correlated replaced token will also be changed back']
      },
      customParamModal: false,
      customParam: DEFAULT_CUSTOM_PARAMETER,
      customKeywordOptions: '',
      goldenQueryParams: {}
    }
  },

  watch: {
    /**
     * Watch function to detect if metaData is mutated, then need to redo test (3rd step)
     */
    'queryMetaData.lines' () {
      this.testPassed = false
    },

    'queryMetaData.author.name' () {
      if (_.isNil(this.queryMetaData.author.name) || !this.users.length) {
        return
      }
      const newEmail = this.users.find((user) => {
        return user.name === this.queryMetaData.author.name
      }).email
      this.$set(this.queryMetaData.author, 'email', newEmail)
    },

    currentStep () {
      // When enter the second step, register the codemirror with mousedown event
      // to replace the token with inputs name
      if (this.currentStep === 1) {
        this.$nextTick(() => {
          this.cmSetMouseEvent()
        })
      }
    },

    /**
     * Watch function used to highlight the placeholders relates to current selected param
     */
    targetParamName () {
      if (this.targetParamName.length) {
        if (this.targetMarkers) {
          this.targetMarkers.forEach(marker => marker.clear())
        }
        if (this.paramsPosMap.hasOwnProperty(this.targetParamName)) {
          this.targetMarkers = []
          const cm = this.$refs.cmParam.cminstance
          this.paramsPosMap[this.targetParamName].forEach(pos => {
            const token = cm.getTokenAt(pos)
            const options = { inclusiveLeft: true, css: 'background-color:lightgreen;', atomic: true }
            this.targetMarkers.push(cm.markText({ line: pos.line, ch: token.start }, { line: pos.line, ch: token.end }, options))
          })
        }
      }
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.queryPad.hasOwnProperty('queryTemplate')) {
      this.$set(this.$root.preferences.queryPad, 'queryTemplate', {})
    }
  },

  created () {
    window.addEventListener('beforeunload', this.saveMetadataToLocalStorage())
    this.fetchAllUsers()
    this.fetchGoldenQueryParams()
    this.loadGoldenQueryAttributes()
    this.setDefaultData()
  },

  mounted () {
    this.setCmArea()
  },

  methods: {
    async fetchGoldenQueryParams () {
      try {
        this.goldenQueryParams = (await axios.get(`${process.env.VUE_APP_API_URI}/golden-query/params`)).data
      } catch (error) {
        console.error(`Failed to fetch golden query parameters: ${error}`)
        this.$Message.error(`Failed to fetch golden query parameters: ${error}`)
      }
    },

    /**
     * Function to set sql scripts properly for display
     */
    setCmArea () {
      this.queryMetaData.rawQuery = setUpQueryFormat(this.queryMetaData.rawQuery)
      this.queryMetaData.lines = setUpQueryFormat(this.queryMetaData.lines)
    },

    /**
     * Prepare the golden query attributes
     */
    loadGoldenQueryAttributes () {
      parameter.forEach(param => {
        this.goldenQuery.parameters[param.name] = { ...param, isSelectable: true }
      })
      this.goldenQuery.categories = category
    },

    /**
     * Auto fill in the default meta data
     */
    setDefaultData () {
      const author = this.$root.preferences.queryPad.queryTemplate.author
      if (!author.name || !author.name.length) {
        this.queryMetaData.author.name = this.defaultUser.name
        this.queryMetaData.author.email = this.defaultUser.email
      }
      this.paramColumns = [
        {
          title: 'Name',
          key: 'name'
        },
        {
          title: 'Description',
          key: 'description'
        },
        {
          title: 'Type',
          key: 'type'
        },
        {
          title: 'Options',
          key: 'options'
        }
      ]
    },

    /**
     * Canel nomination and return back to query pad
     */
    returnToQueryPad () {
      this.$router.push('/query-pad')
    },

    /**
     * Return back to previous step
     */
    previousStep () {
      this.currentStep--
    },

    /**
     * Proceed to the next step
     */
    nextStep () {
      this.$log('query_nominate', 'process', { finishedStep: this.currentStep, queryMetaData: this.queryMetaData })
      this.currentStep++
    },

    /**
     * Save the metadata of query template to local storage
     */
    saveMetadataToLocalStorage () {
      this.$set(this.$root.preferences.queryPad.queryTemplate, 'description', this.queryMetaData.description)
      this.$set(this.$root.preferences.queryPad.queryTemplate, 'tags', this.queryMetaData.tags)
      this.$set(this.$root.preferences.queryPad.queryTemplate, 'name', this.queryMetaData.name)
      this.$set(this.$root.preferences.queryPad.queryTemplate, 'author', this.queryMetaData.author)
      this.$set(this.$root.preferences.queryPad.queryTemplate, 'date', this.queryMetaData.date)
      this.$set(this.$root.preferences.queryPad.queryTemplate, 'rawQuery', this.queryMetaData.rawQuery)
    },

    /**
     * Proceed to the next step of param selection
     */
    nextStepToParamSelection () {
      this.saveMetadataToLocalStorage()
      this.nextStep()
    },

    /**
     * The next step trigger for the second step (towards the template test)
     */
    nextStepToTest () {
      this.queryMetaData.lines = this.$refs.cmParam.content.length ? this.$refs.cmParam.content : this.queryMetaData.lines
      const paramCopy = _.cloneDeep(this.goldenQuery.parameters)
      Object.keys(paramCopy).forEach(item => {
        delete paramCopy[item].isSelectable
      })
      this.queryMetaData.params = Object.keys(this.paramsPosMap).reduce((acc, val) => [...acc, paramCopy[val]], [])
      this.queryMetaDataCopy = _.cloneDeep(this.queryMetaData)
      this.nextStep()
    },

    /**
     * Final step, to submit the template, and store the meta info into mysql db
     */
    async submitTemplate () {
      try {
        await axios.post(process.env.VUE_APP_API_URI + '/golden-query/templates', {
          data: this.queryMetaData,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        this.$Message.success({
          content: 'successfully updated to query template. Now return back to query pad...',
          duration: 3
        })
        this.$log('query_nominate', 'finish', { status: 'succeeed', queryName: this.queryMetaData.name })
        this.returnToQueryPad()
      } catch (error) {
        console.error(error)
        this.$Message.error({
          content: 'Submission failed.',
          duration: 3
        })
        this.$log('query_nominate', 'finish', { status: 'failed', queryMetaData: this.queryMetaData })
      }
    },

    /**
     * Method to check if the meta data has already be filled up
     */
    metaDataFilled () {
      const fillData = this.queryMetaData
      return fillData.description.length && fillData.tags.length
    },

    /**
     * Function triggered when the Select component emits a "change" event
     * Only applies when new parameters are selected (when selectedParamName is defined)
     */
    onAddSelectedParams (selectedParamName) {
      if (!_.isUndefined(selectedParamName) && this.goldenQuery.parameters[selectedParamName].isSelectable) {
        this.goldenQuery.parameters[selectedParamName].isSelectable = false
        this.selectedParamNames.push(selectedParamName)
        this.$nextTick(() => {
          this.targetParamName = selectedParamName
        })
      }
    },

    /**
     * Function triggered when parameters are removed from the list (by click on cross)
     * The corresponding tokens in query respecting to that parameter will be reverted back to its inital value
     */
    onRemoveSelectedParams (event, name) {
      this.goldenQuery.parameters[name].isSelectable = true
      this.selectedParamNames = this.selectedParamNames.filter(n => n !== name)
      if (name === this.$refs.selection.value) {
        this.$refs.selection.clearSingleSelect()
      }
      if (name === this.targetParamName) {
        this.targetParamName = ''
      }
      if (this.paramsPosMap.hasOwnProperty(name)) {
        this.paramsPosMap[name].forEach(pos => {
          const cmOriginal = this.$refs.cmPreview.cminstance
          const cmCurrent = this.$refs.cmParam.cminstance
          const tokenOriginal = cmOriginal.getTokenAt(pos)
          const tokenCurrent = cmCurrent.getTokenAt(pos)
          cmCurrent.replaceRange(tokenOriginal.string, { line: pos.line, ch: tokenCurrent.start }, { line: pos.line, ch: tokenCurrent.end })
        })
      }
      delete this.paramsPosMap[name]
    },

    /**
     * Function used to replace the token with selected param name
     * Trigered when the cursor is placed into the editor: currently by right click
     */
    replaceToken () {
      const cm = this.$refs.cmParam.cminstance
      const pos = cm.getCursor()
      const token = cm.getTokenAt(pos)

      if (token.start === 0 && token.end === 0) {
        return
      }

      if (this.targetParamName) {
        let replaceString = ['number', 'decimal'].includes(this.goldenQuery.parameters[this.targetParamName].type) ? `{{${this.targetParamName}}}` : `'{{${this.targetParamName}}}'`
        replaceString = token.string.startsWith('.') ? `.${replaceString}` : replaceString
        cm.replaceRange(replaceString, { line: pos.line, ch: token.start }, { line: pos.line, ch: token.end })
        if (!this.paramsPosMap.hasOwnProperty(this.targetParamName)) {
          this.paramsPosMap[this.targetParamName] = []
        }
        this.paramsPosMap[this.targetParamName].push(pos)
      }
    },

    /**
     * Triggered when the child component QueryTemplate emit an event "has-results"
     * This function was used to evaluate the results and set property this.testPassed
     */
    checkResults (passed) {
      this.testPassed = passed
      if (this.testPassed) {
        this.$Message.success({
          content: 'Great Job! The test is passed and you can submit the template now.',
          duration: 2
        })
      } else {
        this.$Message.error({
          content: 'The template doesn\'t work.',
          duration: 2
        })
      }
    },

    /**
     * Function used to highlight the token when hovering over it
     */
    highlightToken (e) {
      if (this.hoverTextMarker) {
        this.hoverTextMarker.clear()
      }
      const cm = this.$refs.cmParam.cminstance
      const pos = cm.coordsChar({ left: e.pageX, top: e.pageY }, 'page')
      const token = cm.getTokenAt(pos)
      const options = { inclusiveLeft: false, inclusiveRight: false, selectLeft: false, selectRight: false, css: 'background-color: yellow;', handleMouseEvents: true, atomic: false }
      this.hoverTextMarker = cm.markText({ line: pos.line, ch: token.start }, { line: pos.line, ch: token.end }, options)
    },

    /**
     * Function used to set the mouse event behavior in codemirror properly
     * Currently prevents the default right click behavior when replacing the token
     * TODO: Figure out how to use left click
     */
    cmSetMouseEvent () {
      const cm = this.$refs.cmParam.cminstance
      cm.getWrapperElement().oncontextmenu = e => {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    },

    /**
     * Data transform for custom parameter
     */
    onTransformCustomParam () {
      if (!_.isNil(this.customKeywordOptions) && !_.isEmpty(this.customKeywordOptions)) {
        this.customParam.options = this.customKeywordOptions.split(',').trim()
      }
    },

    /**
     * Add configured custom parameter to golden query parameter base
     * and selected parameters
     */
    onAddCustomParam () {
      this.onTransformCustomParam()
      this.goldenQuery.parameters[this.customParam.name] = this.customParam
      this.selectedParamNames.push(this.customParam.name)
      this.targetParamName = this.customParam.name
      this.onRemoveCustomParam()
    },

    /**
     * Clear the customParam data
     */
    onRemoveCustomParam () {
      this.customParam = DEFAULT_CUSTOM_PARAMETER
    }
  },

  /**
   * Function triggered when user leave current queryPad page
   * The localStorage will be deleted here.
   */
  beforeRouteLeave (to, from, next) {
    this.$delete(this.$root.preferences.queryPad, 'queryTemplate')
    next()
  }
}
</script>

<style scoped>
.steps >>> .ivu-steps-title {
  background-color: var(--color-body-background);
  padding: 2px;
}
.steps >>> .ivu-steps-head {
  background-color: transparent;
}

.steps >>> .ivu-steps-item.ivu-steps-status-process .ivu-steps-head-inner {
  border-color: var(--color-success);
  background-color: var(--color-success);
}
.steps >>> .ivu-steps-item.ivu-steps-status-finish .ivu-steps-head-inner {
  border-color: var(--color-success);
}
.steps >>> .ivu-steps-item.ivu-steps-status-finish .ivu-steps-head-inner span,
.ivu-steps-item.ivu-steps-status-finish .ivu-steps-head-inner.ivu-steps-icon,
.ivu-steps-item.ivu-steps-status-finish .ivu-steps-title {
  color: var(--color-success);
}
.steps >>> .ivu-steps-item.ivu-steps-status-finish .ivu-steps-tail i:after,
.steps >>> .ivu-steps-item.ivu-steps-status-finish .ivu-steps-title:after {
  background: var(--color-success);
}
.meta-data-preview-wrapper {
  padding: 20px;
  margin: 20px;
}

.meta-data-preview-item {
  margin: 5px;
  padding: 5px;
  font-size: 15px;
}

.meta-data-preview-item-label {
  font-weight: bold;
}

.meta-data-preview-item-content {
  margin-top: 8px;
}

.final-step-buttons {
  text-align: left;
  margin-left: 5px;
  padding-top: 20px;
}

.close-icon {
  margin-top: -10px;
  font-size: 25px;
  display: flex;
  justify-content: flex-end;
  color: var(--color-grey-light);
  cursor: pointer;
}

.button-wrapper {
  margin-top: 20px;
}

.query-container {
  height: 100%;
  width: 100% !important;
  border: 1px solid var(--color-gray-border);
}

.selected-param-list {
  margin-bottom: 25x;
}

.instruction-wrapper {
  height: 100%;
  width: 100%;
  background-color: var(--color-green-lighter);
  padding: 10px;
}

.step-form {
  padding-top: 20px;
  width: 60%;
  margin: 0 auto;
}

.step-form >>> .ivu-form-item-label {
  font-weight: bold;
  font-size: 14px;
  text-align: right;
}

.param-form-item >>> .ivu-form-item-label {
  width: 30%;
  text-align: left;
  font-size: 12px;
}

.steps-nav-container {
  position: sticky;
  top: var(--sticky-tab-top);
  padding: 20px 5px 20px 50px;
  z-index: 100;
  background-color: var(--color-body-background);
  border: 1px solid var(--color-gray-border);
  border-bottom: none;
}

.steps-form-container {
  padding: 50px 0 20px 0;
  width: 100%;
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-border);
}

.wizard-container {
  margin-top: -48px;
}

.query-parameters-wrapper >>> .ivu-form-item-content {
  display: flex;
  flex-direction: row;
}
.icon-add-parameter {
  font-size: 25px;
  padding: 3px;
  margin-left: 10px;
  cursor: pointer;
}
.select-query-parameters {
  width: 30%;
}
.custom-param-modal >>> .ivu-form-item {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.custom-param-modal >>> .ivu-form-item-content {
  margin-left: 0 !important;
  width: 70%;
}
.custom-param-modal >>> .ivu-form-item-label {
  width: 20% !important;
  padding-left: 10px;
}
</style>
